import { CalendarDays, Video, Sparkles } from "lucide-react";
import { useInView } from "@/hooks/useAnimations";

const steps = [
  {
    num: "1",
    icon: CalendarDays,
    title: "Termin online buchen",
    desc: "Wählen Sie bequem einen freien Termin über unseren Online-Kalender — rund um die Uhr verfügbar.",
    color: "bg-cyan-700",
    iconColor: "text-white",
    lightBg: "bg-cyan-50",
  },
  {
    num: "2",
    icon: Video,
    title: "Video-Beratung erhalten",
    desc: "Zum vereinbarten Zeitpunkt erhalten Sie einen Link. Ein Klick genügt — keine Installation nötig.",
    color: "bg-green-600",
    iconColor: "text-white",
    lightBg: "bg-green-50",
  },
  {
    num: "3",
    icon: Sparkles,
    title: "Unterstützung & Klarheit",
    desc: "Sie erhalten individuelle Beratung und klare Handlungsempfehlungen für Ihre Pflegesituation.",
    color: "bg-cyan-700",
    iconColor: "text-white",
    lightBg: "bg-cyan-50",
  },
];

export default function HowItWorks() {
  const [ref, isInView] = useInView();

  return (
    <section
      ref={ref}
      id="so-gehts"
      data-testid="how-it-works-section"
      className="py-20 lg:py-28 bg-white scroll-mt-[120px] xl:scroll-mt-[96px]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-sm font-semibold text-green-600 bg-green-50 px-4 py-1.5 rounded-full mb-4">
            In 3 einfachen Schritten
          </span>
          <h2 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            So einfach geht's
          </h2>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
            Innerhalb weniger Minuten zum Beratungstermin — ganz ohne Papierkram.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 relative">
          {/* Connecting line (desktop only) */}
          <div className="hidden md:block absolute top-[72px] left-[16.67%] right-[16.67%] h-[3px] bg-gradient-to-r from-cyan-200 via-green-200 to-cyan-200 rounded-full" />

          {steps.map((step, i) => (
            <div
              key={i}
              data-testid={`step-${i}`}
              className={`relative flex flex-col items-center text-center group transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: isInView ? `${i * 200}ms` : '0ms' }}
            >
              {/* Step number circle */}
              <div className={`relative z-10 h-20 w-20 ${step.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <step.icon className={`h-9 w-9 ${step.iconColor}`} />
                <div className="absolute -top-2 -right-2 h-7 w-7 bg-white border-2 border-slate-100 rounded-full flex items-center justify-center shadow-sm">
                  <span className="text-xs font-bold text-slate-700">{step.num}</span>
                </div>
              </div>

              <h3 className="font-['Manrope'] text-xl sm:text-2xl font-semibold text-slate-800 mb-3">
                {step.title}
              </h3>
              <p className="text-base text-slate-500 leading-relaxed max-w-xs">
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
